import { ulid } from 'ulid';
import { ELEMENT_TYPE } from 'shop-ui/constants';
import {
    type LayoutNode,
    type LayoutNodeContentMap,
    type LayoutNodeStyleMap,
} from 'shop-ui/types';
import { generateGrid, GRID_LAYOUT_TYPE } from 'shop-ui/section/Grid';
import i18n from '@shared/i18n';

export type BasicElementDetail = {
    id: string;
    layout: LayoutNode;
    style: LayoutNodeStyleMap[string];
    content?: LayoutNodeContentMap[string];
};

export type CombinedComponent = {
    layout: LayoutNode[];
    style: LayoutNodeStyleMap[string];
    content: Partial<LayoutNodeContentMap>;
};

export function __test__createTestElementDetail(): BasicElementDetail {
    const id = ulid();

    const layout = {
        id,
        type: ELEMENT_TYPE.TEST_ELEMENT,
        name: 'test-element',
        enable: true,
        next: null,
        deletable: true,
        editable: true,
    };

    const content = '';

    return {
        id,
        layout,
        style: {},
        content,
    };
}

export function __test__createContentElementDetail(): BasicElementDetail {
    const id = ulid();

    const layout = {
        id,
        type: ELEMENT_TYPE.CONTENT,
        name: 'content',
        enable: true,
        next: null,
        deletable: true,
    };

    const content = 'test-content';

    return {
        id,
        layout,
        style: {},
        content,
    };
}

type SecondaryGridType =
    | GRID_LAYOUT_TYPE.SINGLE_CELL
    | GRID_LAYOUT_TYPE.SPLIT_H_2C
    | GRID_LAYOUT_TYPE.SPLIT_H_3C
    | GRID_LAYOUT_TYPE.SPLIT_V_2C
    | GRID_LAYOUT_TYPE.SPLIT_V_3C;

type combineComponentOptions = {
    editable?: boolean;
    deletable?: boolean;
    primaryGridType?: GRID_LAYOUT_TYPE;
    secondaryGridType?: SecondaryGridType | Array<SecondaryGridType>;
    shouldWrap?: boolean;
    primaryGridPresetStyle?: LayoutNodeStyleMap[string];
    secondaryGridPresetStyle?: Array<LayoutNodeStyleMap[string]>;
};

function combineComponent(
    sectionName: string,
    elementDetails: Array<BasicElementDetail | BasicElementDetail[]>,
    options: combineComponentOptions
): CombinedComponent;
function combineComponent(
    sectionName: string,
    elementDetails: Array<Array<BasicElementDetail | BasicElementDetail[]>>,
    options: combineComponentOptions
): CombinedComponent;
function combineComponent(
    sectionName: string,
    elementDetails: BasicElementDetail,
    options: combineComponentOptions
): CombinedComponent;
function combineComponent(
    sectionName: string = i18n.gettext('區塊'),
    elementDetails:
        | Array<BasicElementDetail | BasicElementDetail[]>
        | Array<Array<BasicElementDetail | BasicElementDetail[]>>
        | BasicElementDetail = [],
    options: combineComponentOptions = {}
): CombinedComponent {
    const content: Partial<LayoutNodeContentMap> = {};

    const style: LayoutNodeStyleMap[string] = {};

    function collectData(detail: BasicElementDetail) {
        style[detail.id] = detail.style;

        if (detail.content) {
            content[detail.id] = detail.content;
        }
    }

    function createLayoutEntry(
        details: BasicElementDetail,
        keepObject: boolean
    ): LayoutNode;
    function createLayoutEntry(
        details: Array<BasicElementDetail | BasicElementDetail[]>,
        keepObject?: boolean
    ): Array<LayoutNode>;
    function createLayoutEntry(
        details: Array<BasicElementDetail | BasicElementDetail[]> | BasicElementDetail,
        keepObject = false
    ): Array<LayoutNode> | LayoutNode {
        if (Array.isArray(details)) {
            const [detail, ...remainingDetails] = details;

            if (Array.isArray(detail)) {
                return detail.map((subDetails) => createLayoutEntry(subDetails, true));
            }

            const { layout } = detail;

            if (remainingDetails.length > 0) {
                layout.next = createLayoutEntry(remainingDetails);
            }

            collectData(detail);

            return keepObject || Array.isArray(detail) ? layout : [layout];
        }

        collectData(details);

        return details.layout;
    }

    const primaryLevelGrid = generateGrid.primary(
        sectionName,
        options.primaryGridType,
        options.editable,
        options.deletable,
        options.primaryGridPresetStyle
    );

    const _secondaryLevelGridList = Array(9)
        .fill(null)
        .map((_, index) => {
            const presetStyle = options.secondaryGridPresetStyle?.[index] || {};

            const gridType = Array.isArray(options.secondaryGridType)
                ? options.secondaryGridType[index] || options.secondaryGridType[0]
                : options.secondaryGridType;

            return generateGrid.secondary(index + 1, gridType, presetStyle);
        });

    const secondaryLevelGridList = [];

    for (let i = 0; i < _secondaryLevelGridList.length; i++) {
        //@ts-ignore
        secondaryLevelGridList[i] = [];

        //@ts-ignore
        secondaryLevelGridList[i][0] = _secondaryLevelGridList[i];

        //@ts-ignore
        secondaryLevelGridList[i][1] = Array(3)
            .fill(null)
            .map((_, index) => generateGrid.secondaryInner(index + 1));
    }

    if (Array.isArray(elementDetails) && elementDetails.length > 0) {
        for (let i = 0; i < elementDetails.length; i++) {
            if (Array.isArray(elementDetails[i][0])) {
                //@ts-ignore
                for (let j = 0; j < elementDetails[i].length; j++) {
                    //@ts-ignore
                    secondaryLevelGridList[i][1][j] = [
                        secondaryLevelGridList[i][1][j],
                        elementDetails[i][j],
                    ];
                }
            } else {
                //@ts-ignore
                secondaryLevelGridList[i][1][0] = [
                    secondaryLevelGridList[i][1][0],
                    elementDetails[i],
                ];
            }
        }
    }

    const layout = options?.shouldWrap
        ? createLayoutEntry([primaryLevelGrid, secondaryLevelGridList])
        : // @ts-ignore
          createLayoutEntry(elementDetails);

    return {
        layout,
        style,
        content,
    };
}

export { combineComponent };
